'use client';

import React, { useState } from 'react';
import { useResponsive } from '@/hooks';
import { cn } from '@/utils';
import { IPersona } from '@/types';
import { useVoiceLiveSession } from './hooks';
import { AudioVisualizer } from './components/AudioVisualizer';
import { CaptionsOverlay } from './components/CaptionsOverlay';
import { ConnectionOverlay } from './components/ConnectionOverlay';
import { DegradedBanner } from './components/DegradedBanner';
import { DevFallbackToggle } from './components/DevFallbackToggle';
import { DevTokenUsage } from './components/DevTokenUsage';
import { ListeningIndicator } from './components/ListeningIndicator';
import { PersonaNameTag } from './components/PersonaNameTag';
import { SimulationControlBar } from './components/SimulationControlBar';
import { VideoAudioSwitch } from './components/VideoAudioSwitch';
import { VideoRecommendation } from './components/VideoRecommendation';

export interface TrainingSimulationVoiceLiveProps {
  /** Whether currently in listening mode */
  isListening?: boolean;
  /** Callback when closing the simulation */
  onClose?: () => void;
  /** Callback when exiting the simulation */
  onExit?: () => void;
  /** Callback when toggling captions */
  onToggleCaptions?: () => void;
  /** Callback when toggling camera */
  onToggleCamera?: () => void;
  /** Callback when pausing */
  onPause?: () => void;
  /** Callback when restarting */
  onRestart?: () => void;
  /** Callback when session ID is created */
  onSessionCreated?: (sessionId: number) => void;
  /** User ID */
  userId?: string;
  /** Persona information */
  persona?: IPersona;
  /** Scenario ID */
  scenarioId?: string;
  /** Whether video is enabled when the session starts. Defaults to true.
   * Pass false to start in audio-only mode — no video transceiver is
   * negotiated, saving the full video stream bandwidth from the outset.
   */
  defaultVideoEnabled?: boolean;
}

export const TrainingSimulationVoiceLive: React.FC<TrainingSimulationVoiceLiveProps> = ({
  isListening = false,
  onExit,
  onPause,
  onRestart,
  onSessionCreated,
  onToggleCamera,
  onToggleCaptions,
  persona,
  scenarioId = '1',
  userId = 'dpark21',
  defaultVideoEnabled = true,
}) => {
  const isSmUp = useResponsive({ breakpoint: 'sm', direction: 'min' });

  /**
   * Show captions flag
   */
  const [showCaptions, setShowCaptions] = useState(true);

  const {
    connectionState,
    isActive,
    retryCount,
    handleStart,
    handleStop,
    handleManualRetry,
    avatarState,
    videoRef,
    isDegraded,
    isVideoEnabled,
    handleModeChange,
    showAudioVisualizer,
    showConnectionOverlay,
    errorMessage,
    inputAnalyser,
    outputAnalyser,
    devToolsVisible,
    forceAudioFallback,
    setForceAudioFallback,
    forceIceTimeout,
    setForceIceTimeout,
    showTokenUsage,
    setShowTokenUsage,
    tokenUsageSummary,
  } = useVoiceLiveSession({ userId, scenarioId, defaultVideoEnabled, onSessionCreated });

  /**
   * Handle toggling captions display
   */
  const handleToggleCaptions = () => {
    setShowCaptions(!showCaptions);
    onToggleCaptions?.();
  };

  return (
    <div className="relative flex h-[65vh] w-full flex-col gap-8">
      {/* Main Content Area */}
      <div className="mx-auto h-full max-h-[448px] w-full max-w-[448px]">
        <div className="flex h-full items-center justify-center gap-8">
          {/* Video/Avatar Area */}
          <div
            className={cn(
              'fixed bottom-0 left-0 right-0 top-0 z-10 flex-1 overflow-hidden rounded-none',
              {
                'relative h-full max-h-svh flex-1 overflow-hidden rounded-2xl bg-semantic-globals-page-background shadow-[0px_3.598px_26.983px_0px_rgba(0,0,0,0.15)]':
                  isSmUp,
              },
            )}
          >
            <video
              ref={videoRef}
              className={cn('absolute inset-0 h-full w-full object-cover', {
                hidden: !isVideoEnabled,
                'object-contain': isSmUp,
              })}
              autoPlay
              playsInline
            />

            <AudioVisualizer
              visible={showAudioVisualizer}
              outputAnalyser={outputAnalyser}
              inputAnalyser={inputAnalyser}
              personaName={persona?.name}
            />

            {showConnectionOverlay && (
              <ConnectionOverlay
                connectionState={connectionState}
                avatarState={avatarState}
                retryCount={retryCount}
                errorMessage={errorMessage}
                isSmUp={isSmUp}
                onStart={() => handleStart(userId)}
                onCancel={handleStop}
                onRetry={handleManualRetry}
                onExit={() => {
                  handleStop();
                  onExit?.();
                }}
              />
            )}

            {persona && <PersonaNameTag name={persona.name} isSmUp={isSmUp} />}

            {isListening && <ListeningIndicator />}
          </div>
        </div>

        {showCaptions && !isListening && <CaptionsOverlay onClose={handleToggleCaptions} />}
      </div>

      <SimulationControlBar
        showCaptions={showCaptions}
        isActive={isActive}
        onToggleCaptions={handleToggleCaptions}
        onToggleCamera={onToggleCamera}
        onPause={onPause}
        onRestart={onRestart}
        onExit={() => {
          handleStop();
          onExit?.();
        }}
      />

      {isDegraded && <DegradedBanner isSmUp={isSmUp} />}

      <VideoRecommendation isVideoEnabled={isVideoEnabled} />

      <VideoAudioSwitch
        isSmUp={isSmUp}
        isVideoEnabled={isVideoEnabled}
        isDegraded={isDegraded}
        onModeChange={handleModeChange}
        onExit={() => {
          handleStop();
          onExit?.();
        }}
      />

      {devToolsVisible && (
        <DevFallbackToggle
          forceAudioFallback={forceAudioFallback}
          onToggle={() => setForceAudioFallback((prev) => !prev)}
          forceIceTimeout={forceIceTimeout}
          onToggleIceTimeout={() => setForceIceTimeout((prev) => !prev)}
          showTokenUsage={showTokenUsage}
          onToggleTokenUsage={() => setShowTokenUsage((prev) => !prev)}
        />
      )}

      {devToolsVisible && showTokenUsage && <DevTokenUsage summary={tokenUsageSummary} />}
    </div>
  );
};

export default TrainingSimulationVoiceLive;
